import { useMemo, useState } from "react"
import { Badge, Box, Button, Flex, Heading, HStack, Input, Text, Textarea, VStack, createListCollection } from "@chakra-ui/react"
import { SelectContent, SelectItem, SelectRoot, SelectTrigger, SelectValueText } from "@chakra-ui/react"

import ErrorAlert from "../../components/ErrorAlert"
import SectionCard from "../../framework/SectionCard"
import { extractErrorInfo } from "../../lib/api/error-utils"
import { formatDateTime } from "../../utils/datetime"
import { useCreatePreparedPush } from "./hooks/useCreatePreparedPush"
import { useDeletePreparedPush } from "./hooks/useDeletePreparedPush"
import { usePreparedPushes } from "./hooks/usePreparedPushes"
import { useUpdatePreparedPush } from "./hooks/useUpdatePreparedPush"
import type { PreparedPushNotificationItem, PreparedPushStatus } from "./types"

const STATUS_OPTIONS = createListCollection({
  items: [
    { value: "draft", label: "Draft" },
    { value: "ready", label: "Ready to send" },
    { value: "paused", label: "Paused" },
  ],
})

const statusColorMap: Record<PreparedPushStatus, string> = {
  draft: "gray",
  ready: "blue",
  paused: "orange",
  sending: "purple",
  sent: "green",
}

function toEditableStatus(status: PreparedPushStatus): PreparedPushStatus {
  if (status === "draft" || status === "ready" || status === "paused") {
    return status
  }
  return "paused"
}

export default function PreparedPushesDashboard() {
  const { data, isLoading, error } = usePreparedPushes()
  const createMutation = useCreatePreparedPush()
  const updateMutation = useUpdatePreparedPush()
  const deleteMutation = useDeletePreparedPush()

  const [selectedUid, setSelectedUid] = useState<string | null>(null)
  const [title, setTitle] = useState("")
  const [message, setMessage] = useState("")
  const [minAppVersion, setMinAppVersion] = useState("")
  const [status, setStatus] = useState<PreparedPushStatus>("draft")
  const [localError, setLocalError] = useState<string | null>(null)

  const rows = useMemo(() => data?.Notifications ?? [], [data])
  const selected = useMemo(
    () => rows.find((row) => row.Uid === selectedUid) ?? null,
    [rows, selectedUid],
  )

  const apiErrorInfo = useMemo(() => {
    if (!error) return null
    return extractErrorInfo(error)
  }, [error])

  const isEditing = Boolean(selected)
  const isSaving = createMutation.isPending || updateMutation.isPending
  const isDeleting = deleteMutation.isPending

  const resetForm = () => {
    setSelectedUid(null)
    setTitle("")
    setMessage("")
    setMinAppVersion("")
    setStatus("draft")
    setLocalError(null)
  }

  const loadForEdit = (item: PreparedPushNotificationItem) => {
    setSelectedUid(item.Uid)
    setTitle(item.Title)
    setMessage(item.Message)
    setMinAppVersion(item.MinAppVersion || "")
    setStatus(toEditableStatus(item.Status))
    setLocalError(null)
  }

  const validate = (): boolean => {
    if (!title.trim()) {
      setLocalError("Title is required")
      return false
    }
    if (!message.trim()) {
      setLocalError("Message is required")
      return false
    }
    if (!minAppVersion.trim()) {
      setLocalError("Min app version is required")
      return false
    }
    if (!/^\d+\.\d+\.\d+$/.test(minAppVersion.trim())) {
      setLocalError("Min app version must be in format major.minor.patch")
      return false
    }
    return true
  }

  const handleSubmit = async () => {
    setLocalError(null)
    if (!validate()) return

    try {
      if (isEditing && selectedUid) {
        await updateMutation.mutateAsync({
          uid: selectedUid,
          data: {
            Title: title.trim(),
            Message: message.trim(),
            MinAppVersion: minAppVersion.trim(),
            Status: status,
          },
        })
      } else {
        await createMutation.mutateAsync({
          Title: title.trim(),
          Message: message.trim(),
          MinAppVersion: minAppVersion.trim(),
          Status: status,
        })
      }
      resetForm()
    } catch (err) {
      const info = extractErrorInfo(err)
      setLocalError(info.message)
    }
  }

  const handleDelete = async (item: PreparedPushNotificationItem) => {
    if (!confirm(`Delete prepared push "${item.Title}"?`)) {
      return
    }

    try {
      await deleteMutation.mutateAsync(item.Uid)
      if (selectedUid === item.Uid) {
        resetForm()
      }
    } catch (err) {
      const info = extractErrorInfo(err)
      setLocalError(info.message)
    }
  }

  return (
    <Box p="6">
      <Heading size="2xl" mb="6">
        Prepared Push Notifications
      </Heading>

      {apiErrorInfo && (
        <ErrorAlert message={apiErrorInfo.message} traceId={apiErrorInfo.traceId} />
      )}

      <Flex gap="6" direction={{ base: "column", xl: "row" }}>
        <Box flex="1">
          <SectionCard title={isEditing ? "Edit Prepared Push" : "Create Prepared Push"}>
            <VStack align="stretch" gap="4">
              <Box>
                <Text fontSize="sm" mb="2" color="gray.300">Status</Text>
                <SelectRoot
                  collection={STATUS_OPTIONS}
                  value={[status]}
                  onValueChange={(e) => setStatus((e.value[0] ?? "draft") as PreparedPushStatus)}
                  disabled={isSaving}
                  size="sm"
                >
                  <SelectTrigger bg="gray.700">
                    <SelectValueText placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent bg="gray.800" borderColor="gray.600">
                    {STATUS_OPTIONS.items.map((option) => (
                      <SelectItem key={option.value} item={option} _hover={{ bg: "gray.700" }}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </SelectRoot>
              </Box>

              <Box>
                <Text fontSize="sm" mb="2" color="gray.300">Title</Text>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Quick favor?"
                  bg="gray.700"
                  disabled={isSaving}
                />
              </Box>

              <Box>
                <Text fontSize="sm" mb="2" color="gray.300">Message</Text>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="e.g., Could you rate the app? It helps us a lot."
                  bg="gray.700"
                  rows={4}
                  disabled={isSaving}
                />
              </Box>

              <Box>
                <Text fontSize="sm" mb="2" color="gray.300">Min App Version</Text>
                <Input
                  value={minAppVersion}
                  onChange={(e) => setMinAppVersion(e.target.value)}
                  placeholder="e.g., 1.24.0"
                  bg="gray.700"
                  disabled={isSaving}
                />
              </Box>

              {localError && <ErrorAlert message={localError} />}

              <HStack gap="3">
                <Button
                  size="sm"
                  colorPalette="blue"
                  onClick={handleSubmit}
                  loading={isSaving}
                >
                  {isEditing ? "Save Changes" : "Create Push"}
                </Button>
                {isEditing && (
                  <Button
                    size="sm"
                    variant="surface"
                    colorPalette="gray"
                    onClick={resetForm}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                )}
              </HStack>
            </VStack>
          </SectionCard>
        </Box>

        <Box flex="2">
          <SectionCard title="All Prepared Pushes">
            {isLoading ? (
              <Flex align="center" justify="center" py="8">
                <Text fontSize="sm">Loading…</Text>
              </Flex>
            ) : rows.length === 0 ? (
              <Text fontSize="sm" color="gray.400">No prepared pushes yet.</Text>
            ) : (
              <Box overflowX="auto">
                <Box as="table" width="100%">
                  <Box as="thead">
                    <Box as="tr">
                      <Box as="th" px="3" py="2" borderBottomWidth="1px" borderColor="gray.700" textAlign="left" fontSize="xs" textTransform="uppercase" letterSpacing="wide">Title</Box>
                      <Box as="th" px="3" py="2" borderBottomWidth="1px" borderColor="gray.700" textAlign="left" fontSize="xs" textTransform="uppercase" letterSpacing="wide">Status</Box>
                      <Box as="th" px="3" py="2" borderBottomWidth="1px" borderColor="gray.700" textAlign="left" fontSize="xs" textTransform="uppercase" letterSpacing="wide">Stats</Box>
                      <Box as="th" px="3" py="2" borderBottomWidth="1px" borderColor="gray.700" textAlign="left" fontSize="xs" textTransform="uppercase" letterSpacing="wide">Updated</Box>
                      <Box as="th" px="3" py="2" borderBottomWidth="1px" borderColor="gray.700" textAlign="left" fontSize="xs" textTransform="uppercase" letterSpacing="wide">Actions</Box>
                    </Box>
                  </Box>
                  <Box as="tbody">
                    {rows.map((row) => (
                      <Box as="tr" key={row.Uid}>
                        <Box as="td" px="3" py="2" borderBottomWidth="1px" borderColor="gray.700" verticalAlign="top">
                          <VStack align="start" gap="1">
                            <Text fontSize="sm" fontWeight="600">{row.Title}</Text>
                            <Text fontSize="xs" color="gray.400" maxW="480px" whiteSpace="pre-wrap">
                              {row.Message}
                            </Text>
                            <Text fontSize="xs" color="gray.300">
                              Min version: {row.MinAppVersion || "—"}
                            </Text>
                            <Text fontSize="xs" color="gray.500" fontFamily="mono">
                              {row.Uid}
                            </Text>
                          </VStack>
                        </Box>
                        <Box as="td" px="3" py="2" borderBottomWidth="1px" borderColor="gray.700" verticalAlign="top">
                          <Badge colorPalette={statusColorMap[row.Status]}>{row.Status}</Badge>
                        </Box>
                        <Box as="td" minW="160px" px="3" py="2" borderBottomWidth="1px" borderColor="gray.700" verticalAlign="top">
                          <Text fontSize="sm" whiteSpace="nowrap">Eligible: {row.EligibleCount}</Text>
                          <Text fontSize="sm" whiteSpace="nowrap">Sent: {row.SentCount}</Text>
                          <Text fontSize="sm" whiteSpace="nowrap">Remaining: {Math.max(row.EligibleCount - row.SentCount, 0)}</Text>
                          <Text fontSize="sm" whiteSpace="nowrap" color={row.FailedCount > 0 ? "red.300" : "gray.400"}>
                            Failed: {row.FailedCount}
                          </Text>
                        </Box>
                        <Box as="td" px="3" py="2" borderBottomWidth="1px" borderColor="gray.700" verticalAlign="top">
                          <VStack align="start" gap="1">
                            <Text fontSize="xs" color="gray.300">{formatDateTime(row.UpdatedAt)}</Text>
                            {row.LastRunStartedAt && (
                              <Text fontSize="xs" color="gray.500">
                                run: {formatDateTime(row.LastRunStartedAt)}
                              </Text>
                            )}
                          </VStack>
                        </Box>
                        <Box as="td" px="3" py="2" borderBottomWidth="1px" borderColor="gray.700" verticalAlign="top">
                          <HStack gap="2">
                            <Button size="xs" variant="surface" colorPalette="blue" onClick={() => loadForEdit(row)}>
                              Edit
                            </Button>
                            <Button
                              size="xs"
                              colorPalette="red"
                              variant="surface"
                              onClick={() => handleDelete(row)}
                              loading={isDeleting}
                              disabled={isDeleting}
                            >
                              Delete
                            </Button>
                          </HStack>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            )}
          </SectionCard>
        </Box>
      </Flex>
    </Box>
  )
}
